/* eslint-disable no-console */
/* eslint-disable max-len */
import { CircularProgress } from '@material-ui/core';
import { EntityBlobDefinition, EntityClass, EntityMetricsDefinition, EntityMetricsDefinitionLinkTo, SmartModuleType }
    from '@moderepo/mode-internal-apis';
import React, { CSSProperties, useMemo } from 'react';
import { MOBILITY_DEFAULT_HIGH_SPEED_COLOR, MOBILITY_DEFAULT_ROUTE_LINE_THICKNESS, MOBILITY_DEFAULT_LOW_SPEED_COLOR, MOBILITY_DEFAULT_VEHICLE_COLOR,
    MOBILITY_DEFAULT_VEHICLE_ENTITY_CLASS, useFetchMultipleTripsData, EventMarkerIcon, MOBILITY_DEFAULT_LOCATION_METRICS_DEFINITION_ID,
    MOBILITY_DEFAULT_EVENT_METRICS_DEFINITION_ID, MOBILITY_DEFAULT_VEHICLE_ASSIGNMENT_EVENT_METRICS_DEFINITION_ID, useFetchTripsInfoData,
    MOBILITY_DEFAULT_TRIP_EVENT_METRICS_DEFINITION_ID, TripInfoDataSettings, TripLocationDataSettings, TripEventDataSettings,
    isTripInfoDataSettings, isTripLocationDataSettings, isTripEventDataSettings } from '.';
import { useConvertTimeControlParamsToFetchDataTimeRangeParams } from '../..';
import { EntityUIComponentCollection, EntityUIComponentProps, useSelectTranslatedEntityClasses } from '../../..';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../../../utils/logger';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue }
    from '../../../../../utils/utils';
import { TripsRouteMapContainer } from './TripsRouteMapContainer';



export interface EventMarkerIconSetting {
    readonly icon?: EventMarkerIcon | undefined;

    readonly size?: number | undefined;

    // The color of the incident marker's background
    readonly background?: string | undefined;

    // The color of the incident marker's foreground, for example the ! symbol
    readonly foreground?: string | undefined;
}


/**
 * type guard function to check if an object is a type of EventMarkerIconSetting
 */
const isEventMarkerIconSetting = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EventMarkerIconSetting => {
    const settings = obj as EventMarkerIconSetting;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon, false, false, Object.values(EventMarkerIcon))) {
        return errorLogger?.logError({
            message: `"icon" must be undefined or one of the following [${Object.values(EventMarkerIcon).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.icon,
            path : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.size, false, true, 1, 5)) {
        return errorLogger?.logError({
            message: '"size" must be undefined or a number between 1 and 5.',
            value  : settings.size,
            path   : [...configPath, 'size'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.background)) {
        return errorLogger?.logError({
            message: '"background" must be undefined or a color string.',
            value  : settings.background,
            path   : [...configPath, 'background'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.foreground)) {
        return errorLogger?.logError({
            message: '"foreground" must be undefined or a color string.',
            value  : settings.foreground,
            path   : [...configPath, 'foreground'],
        }) ?? false;
    }

    return true;
};



export interface EntityTripsRouteMapUIComponentCustomSettings {

    // Whether the entity that this UI Component is showing is a Driver entity. The default is FALSE which means the component will assume
    // the selected entity is a Vehicle and will display trip route data for the vehicle.
    // This component can be used for displaying Trips route data for a Vehicle or a Driver.
    readonly isDriver?: boolean | boolean;

    // The class name of the entity class definition for Vehicles. If the selected entity for this component is a DRIVER then this
    // component will need to know which entity class is the "Vehicle" class so that it can look for "location" and "events" metrics
    // definition from that entity class.
    // By default, the component will assume the project has an entity class call "vehicle" and use that entity class. If the project use
    // a different entity class, the admin must configure this attribute.
    readonly vehicleEntityClass?: string | undefined;


    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // The custom style for the element that wraps the Map component
    readonly mapContainerStyle?: CSSProperties | undefined;

    // To specify where the trip info data are stored e.g. which metrics definition they are stored in and what the name of the metrics are.
    // There are default values for these settings therefore, they are only needed for projects that have different set up.
    readonly tripsInfoDataSettings?: TripInfoDataSettings | undefined;


    // To specify where the route location data are stored e.g. which metrics definition they are stored in and what the name of the metrics are.
    // There are default values for these settings therefore, they are only needed for projects that have different set up.
    readonly locationDataSettings?: TripLocationDataSettings | undefined;


    // Whether or not to show events on the map. The default is TRUE unless this is specifically set to false
    readonly showEvents?: boolean | undefined;


    // To specify where the route event data are stored e.g. which metrics definition they are stored in and what the name of the metrics are.
    // There are default values for these settings therefore, they are only needed for projects that have different set up.
    readonly eventDataSettings?: TripEventDataSettings | undefined;


    readonly routeUISettings?: {
        // Whether or not to use different line color to draw the route based on the vehicle's speed. The default is FALSE unless
        // this is specifically set to true. This option will cause the component to create many PolyLine elements therefore, only turn this on
        // if really necessary.
        readonly showVehicleSpeed?: boolean | undefined;

        // The colors to use to draw the route. If "showVehicleSpeed" is false, only 1 color is required. If "showVehicleSpeed" is set to true, 2 colors
        // must be provided. The first color will be used for slowest speed and the second color is used for the fastest speed.
        readonly lineColors?: [string, ...string[]] | undefined;

        // The thickness of the route line. The value must be between 5 and 10.
        readonly lineThickness?: number | undefined;

        // The color of the vehicle
        readonly vehicleColor?: string | undefined;

        // The size of the vehicle. The values must be between 1 and 5, decimals numbers are accepted e.g. 1.5
        readonly vehicleSize?: number | undefined;

        // The GLOBAL event marker icon settings. This is for configuring the Icon and color to use for displaying event marker icons on the map.
        // This settings will apply to ALL events
        readonly eventMarkerIconsSettings?: EventMarkerIconSetting | undefined;

        // Event marker icon settings by EVENT TYPE. If a project needs to use a different icon for some specific event type then this can be used
        // to configure the icon for those event type. If this does not have settings for an event type, then the global "eventMarkerIconsSettings"
        // will be used
        readonly eventMarkerIconsSettingsByEventType?: {
            readonly [eventType: string]: EventMarkerIconSetting | undefined;
        } | undefined;
    } | undefined;
}



/**
 * type guard function to check if an object is a type of EntityTripsRouteMapUIComponentCustomSettings
 */
const isEntityTripsRouteMapUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityTripsRouteMapUIComponentCustomSettings => {
    const settings = obj as EntityTripsRouteMapUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.isDriver)) {
        return errorLogger?.logError({
            message: '"isDriver" must be undefined or a boolean.',
            value  : settings.isDriver,
            path   : [...configPath, 'isDriver'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.vehicleEntityClass)) {
        return errorLogger?.logError({
            message: '"vehicleEntityClass" must be undefined or a non-empty string.',
            value  : settings.vehicleEntityClass,
            path   : [...configPath, 'vehicleEntityClass'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (settings.mapContainerStyle !== undefined && !isObjectLiteral(settings.mapContainerStyle)) {
        return errorLogger?.logError({
            message: '"mapContainerStyle" must be undefined or a CSSProperties object.',
            value  : settings.mapContainerStyle,
            path   : [...configPath, 'mapContainerStyle'],
        }) ?? false;
    }

    // tripsInfoDataSettings is optional but if it is provided then it must have the right values
    if (
        settings.tripsInfoDataSettings !== undefined
        && !isTripInfoDataSettings(settings.tripsInfoDataSettings, errorLogger, [...configPath, 'tripsInfoDataSettings'])
    ) {
        return false;
    }

    // locationDataSettings is optional but if it is provided then it must have the right values
    if (
        settings.locationDataSettings !== undefined
        && !isTripLocationDataSettings(settings.locationDataSettings, errorLogger, [...configPath, 'locationDataSettings'])
    ) {
        return false;
    }

    if (!isValidSettingBooleanValue(settings.showEvents)) {
        return errorLogger?.logError({
            message: '"showEvents" must be undefined or a boolean.',
            value  : settings.showEvents,
            path   : [...configPath, 'showEvents'],
        }) ?? false;
    }

    // eventDataSettings is optional but if it is provided then it must have the right values
    if (
        settings.eventDataSettings !== undefined
        && !isTripEventDataSettings(settings.eventDataSettings, errorLogger, [...configPath, 'eventDataSettings'])
    ) {
        return false;
    }


    // routeUISettings is optional but if it is provided then it must have the right values
    if (settings.routeUISettings !== undefined) {
        if (!isObjectLiteral(settings.routeUISettings)) {
            return errorLogger?.logError({
                message: '"routeUISettings" must be undefined or an Object.',
                value  : settings.routeUISettings,
                path   : [...configPath, 'routeUISettings'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.routeUISettings.showVehicleSpeed)) {
            return errorLogger?.logError({
                message: '"showVehicleSpeed" must be undefined or a boolean.',
                value  : settings.routeUISettings.showVehicleSpeed,
                path   : [...configPath, 'routeUISettings', 'showVehicleSpeed'],
            }) ?? false;
        }

        if (!isValidSettingNumberValue(settings.routeUISettings.lineThickness, false, false, 5, 10)) {
            return errorLogger?.logError({
                message: '"lineThickness" must be undefined or an integer between 5 and 10.',
                value  : settings.routeUISettings.lineThickness,
                path   : [...configPath, 'routeUISettings', 'lineThickness'],
            }) ?? false;
        }

        if (!isValidSettingNumberValue(settings.routeUISettings.vehicleSize, false, true, 1, 5)) {
            return errorLogger?.logError({
                message: '"vehicleSize" must be undefined or a number between 1 and 5.',
                value  : settings.routeUISettings.vehicleSize,
                path   : [...configPath, 'routeUISettings', 'vehicleSize'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.routeUISettings.vehicleColor)) {
            return errorLogger?.logError({
                message: '"vehicleColor" must be undefined or a color string.',
                value  : settings.routeUISettings.vehicleColor,
                path   : [...configPath, 'routeUISettings', 'vehicleColor'],
            }) ?? false;
        }

        if (settings.routeUISettings.lineColors !== undefined) {
            if (!(settings.routeUISettings.lineColors instanceof Array)) {
                return errorLogger?.logError({
                    message: '"lineColors" must be undefined or an Array of color strings.',
                    value  : settings.routeUISettings.lineColors,
                    path   : [...configPath, 'routeUISettings', 'lineColors'],
                }) ?? false;
            }

            if (!settings.routeUISettings.lineColors.every((color, index) => {
                if (!isValidSettingStringValue(color)) {
                    return errorLogger?.logError({
                        message: 'Every values in "lineColors" array must be color strings.',
                        value  : color,
                        path   : [...configPath, 'routeUISettings', 'lineColors', `[${index}]`],
                    }) ?? false;
                }
                return true;
            })) {
                return false;
            }
        }

        // routeUISettings.eventMarkerIconsSettings is optional but if it is provided then it must be valid object which EACH value
        // in the object must be an EventMarkerIconSetting
        if (
            settings.routeUISettings.eventMarkerIconsSettings !== undefined
            && !isEventMarkerIconSetting(
                settings.routeUISettings.eventMarkerIconsSettings, errorLogger, [...configPath, 'routeUISettings', 'eventMarkerIconsSettings'],
            )
        ) {
            return false;
        }


        // routeUISettings.eventMarkerIconsSettingsByEventType is optional but if it is provided then it must be valid object which EACH value
        // in the object must be an EventMarkerIconSetting
        if (settings.routeUISettings.eventMarkerIconsSettingsByEventType !== undefined) {
            if (!isObjectLiteral(settings.routeUISettings.eventMarkerIconsSettingsByEventType)) {
                return errorLogger?.logError({
                    message: '"eventMarkerIconsSettingsByEventType" must be undefined or an Object.',
                    value  : settings.routeUISettings.eventMarkerIconsSettingsByEventType,
                    path   : [...configPath, 'routeUISettings', 'eventMarkerIconsSettingsByEventType'],
                }) ?? false;
            }

            if (!(Object.entries(settings.routeUISettings.eventMarkerIconsSettingsByEventType).every(([eventType, iconSettings]) => {
                return isEventMarkerIconSetting(
                    iconSettings, errorLogger, [...configPath, 'routeUISettings', 'eventMarkerIconsSettingsByEventType', `${eventType}`],
                );
            }))) {
                return false;
            }
        }
    }


    return true;
};


/**
 * Get the blob definition from the vehicleEntityClass that is a type of "VIDEO" AND is LINKED TO one of the eventMetricsDefinition's linksTo.
 * Return BOTH the EntityBlobDefinition and the EntityMetricsDefinitionLinkTo. This function return something like this
 *      {
 *          eventVideoLinkToInfo: {
 *              blobDefinitionId: "vehicle_event_video",
 *              keyField: "event_key"
 *          },
 *          videoBlobDefinition: {
 *              blobDefinitionId: "vehicle_event_video",
 *              displayName: "Vehicle Event Video",
 *              dataStoreConfiguration: {
 *                  moduleInstanceId: "video",
 *                  moduleType: "video"
 *              }
 *          }
 *      }
 */
const getVideoDataSourceInfo = (
    vehicleEntityClass: EntityClass,
    eventMetricsDefinition: EntityMetricsDefinition | undefined,
): {
    readonly eventVideoLinkToInfo: EntityMetricsDefinitionLinkTo;
    readonly videoBlobDefinition: EntityBlobDefinition;
} | undefined => {

    const blobDefinitions = vehicleEntityClass.blobDefinitions;
    const linksTo = eventMetricsDefinition?.linksTo;

    // if the metricsDefinition does not have "linksTo" info then we can assume it does not have videos associated with it
    // Or if entityClass does not have blobDefinitions then we can assume there is no video
    if ((!linksTo || linksTo.length === 0) || (!blobDefinitions || blobDefinitions.length === 0)) {
        return undefined;
    }

    // linksTo is an array containing links to BlobDefinitions. HOWEVER, not all BlobDefinitions are for Videos therefore, we need to loop through
    // all the entityClass' blobDefinitions and find one that is a VIDEO blobDefinition AND matches one of the linksTo
    const result = linksTo.reduce((
        result: {
            readonly eventVideoLinkToInfo: EntityMetricsDefinitionLinkTo;
            readonly videoBlobDefinition: EntityBlobDefinition;
        } | undefined,
        linkTo,
    ) => {
        if (!result) {
            const videoBlobDefinition = blobDefinitions.find((blobDef) => {
                return blobDef.blobDefinitionId === linkTo.blobDefinitionId && blobDef.dataStoreConfiguration.moduleType === SmartModuleType.VIDEO;
            });

            if (videoBlobDefinition) {
                // We found a BlobDefinition that matches one of the linksTo, we can use these 2 as the result.
                return {
                    eventVideoLinkToInfo: linkTo,
                    videoBlobDefinition,
                };
            }
        }
        return result;
    }, undefined);


    return result;
};



export interface EntityTripsRouteMapUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityTripsRouteMapUIComponentCustomSettings | undefined;
}


export const EntityTripsRouteMapUIComponent: React.FC<EntityTripsRouteMapUIComponentProps> = (props: EntityTripsRouteMapUIComponentProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);


    /**
     * Fetch data for ALL the entityClasses, the selected entity. Then find the entityClass that the selected entity belongs to and also
     * find the metrics definition from the entity class where we can fetch event data from.
     */
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    // Get the entity class that the selected entity belonged to
    const selectedEntityClass = useMemo(() => {
        return entityClasses?.find((entityClass) => {
            return entityClass.entityClass === selectedEntity?.entityClass;
        });
    }, [entityClasses, selectedEntity?.entityClass]);


    // Find the vehicle entity class if this component's selected entity IS a DRIVER, not vehicle
    const vehicleEntityClass = useMemo(() => {
        if (props.customSettings?.isDriver) {
            const vehicleClassName = props.customSettings?.vehicleEntityClass ?? MOBILITY_DEFAULT_VEHICLE_ENTITY_CLASS;
            return entityClasses?.find((entityClass) => {
                return entityClass.entityClass === vehicleClassName;
            });
        }

        // If this component's selected entity IS NOT a driver then we can assume it is a Vehicle class therefore it should be the same class
        // as the selected entity class
        return selectedEntityClass;
    }, [entityClasses, props.customSettings?.isDriver, props.customSettings?.vehicleEntityClass, selectedEntityClass]);


    /**
     * To fetch location and events data, we will need to get the metrics definition where these data are stored. The info about the metrics
     * definitions are defined in the "customSettings" object. If these are not defined, use the default location and event metrics definitions.
     */
    const {
        tripEventMetricsDefinition, vehicleAssignmentEventMetricsDefinition,
        locationMetricsDefinition, eventMetricsDefinition, eventVideoLinkToInfo, videoBlobDefinition,
    } = useMemo(() => {
        if (selectedEntityClass && vehicleEntityClass && props.customSettings) {

            // Get the ID of the metrics definition where trips info are stored. Use default ID if not provided.
            const tripsEventMetricsDefinitionId = props.customSettings?.tripsInfoDataSettings?.metricsDefinitionId ?? MOBILITY_DEFAULT_TRIP_EVENT_METRICS_DEFINITION_ID;
            const tripEventMetricsDefinition = selectedEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === tripsEventMetricsDefinitionId;
            });

            // To fetch trip info data, we need to use the "vehicle_assignment_event"s metricsDefinitionId therefore we need to the metricsDefinition
            // object for this metricsDefinition
            const vehicleAssignmentEventMetricsDefinitionId = props.customSettings?.tripsInfoDataSettings?.vehicleAssignmentEventMetricsDefinitionId
                                                                 ?? MOBILITY_DEFAULT_VEHICLE_ASSIGNMENT_EVENT_METRICS_DEFINITION_ID;
            const vehicleAssignmentEventMetricsDefinition = selectedEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === vehicleAssignmentEventMetricsDefinitionId;
            });

            // Get the ID of the metrics definition where location data are stored. Use default ID if not provided.
            const locationMetricsDefinitionId = props.customSettings?.locationDataSettings?.metricsDefinitionId ?? MOBILITY_DEFAULT_LOCATION_METRICS_DEFINITION_ID;
            const locationMetricsDefinition = vehicleEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === locationMetricsDefinitionId;
            });

            // Get the metricsDefinitions required for loading events and event videos data IF showEvents is true
            const { eventMetricsDefinitionId, eventMetricsDefinition, eventVideoDataSourceInfo } = (() => {
                if (props.customSettings.showEvents === false) {
                    return { eventMetricsDefinitionId: undefined, eventMetricsDefinition: undefined, eventVideoDataSourceInfo: undefined };
                }

                // Get the ID of the metrics definition where events data are stored. Use default ID if not provided.
                const eventMetricsDefinitionId = props.customSettings?.eventDataSettings?.metricsDefinitionId ?? MOBILITY_DEFAULT_EVENT_METRICS_DEFINITION_ID;
                const eventMetricsDefinition = vehicleEntityClass.metricsDefinitions?.find((metricsDef) => {
                    return metricsDef.metricsDefinitionId === eventMetricsDefinitionId;
                });

                // Get the blobDefinition and linkTo object which is used for telling the front end how to fetch video data.
                const eventVideoDataSourceInfo = getVideoDataSourceInfo(vehicleEntityClass, eventMetricsDefinition);

                return { eventMetricsDefinitionId, eventMetricsDefinition, eventVideoDataSourceInfo };
            })();



            // If we can't find some of the metricsDefinition, write some error messages in the console so that it is easier to debug the settings.
            if (!tripEventMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${tripsEventMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }
            if (!vehicleAssignmentEventMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${vehicleAssignmentEventMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }
            if (!locationMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${locationMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }
            if (props.customSettings.showEvents !== false && !eventMetricsDefinition) {
                console.error(`Unable to find metrics definitions for '${eventMetricsDefinitionId}'. Please check the customSettings or entity class definition`);
            }

            return {
                tripEventMetricsDefinition,
                vehicleAssignmentEventMetricsDefinition,
                locationMetricsDefinition,
                eventMetricsDefinition,
                eventVideoLinkToInfo: eventVideoDataSourceInfo?.eventVideoLinkToInfo,
                videoBlobDefinition : eventVideoDataSourceInfo?.videoBlobDefinition,
            };
        }

        return {
            tripEventMetricsDefinition             : undefined,
            vehicleAssignmentEventMetricsDefinition: undefined,
            locationMetricsDefinition              : undefined,
            eventMetricsDefinition                 : undefined,
            eventVideoLinkToInfo                   : undefined,
            videoBlobDefinition                    : undefined,
        };
    }, [selectedEntityClass, vehicleEntityClass, props.customSettings]);


    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataTimeRange = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);


    /**
     * Fetch the trips for the selectedEntity for the specified start/end time.
     */
    const tripsInfo = useFetchTripsInfoData(
        props.projectId,
        props.entityId,
        fetchDataTimeRange?.startTime,
        fetchDataTimeRange?.endTime,
        undefined,
        tripEventMetricsDefinition,
        vehicleAssignmentEventMetricsDefinition,
        props.customSettings?.tripsInfoDataSettings,
    );


    /**
     * Fetch Trips data for the provided trips info
     */
    const tripsData = useFetchMultipleTripsData(
        props.projectId,
        selectedEntity?.homeId,
        locationMetricsDefinition,
        eventMetricsDefinition,
        eventVideoLinkToInfo,
        videoBlobDefinition,
        props.customSettings,
        tripsInfo,
    );



    return (
        <>
            {selectedEntity && locationMetricsDefinition && tripsData && (
                <TripsRouteMapContainer
                    mapContainerStyle={props.customSettings?.mapContainerStyle}
                    projectId={props.projectId}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : undefined}
                    icon={props.customSettings?.icon}
                    tripsData={tripsData}
                    speedColors={props.customSettings?.routeUISettings?.lineColors ?? [MOBILITY_DEFAULT_LOW_SPEED_COLOR, MOBILITY_DEFAULT_HIGH_SPEED_COLOR]}
                    lineThickness={props.customSettings?.routeUISettings?.lineThickness ?? MOBILITY_DEFAULT_ROUTE_LINE_THICKNESS}
                    showVehicleSpeed={props.customSettings?.routeUISettings?.showVehicleSpeed}
                    vehicleColor={props.customSettings?.routeUISettings?.vehicleColor ?? MOBILITY_DEFAULT_VEHICLE_COLOR}
                    vehicleSize={props.customSettings?.routeUISettings?.vehicleSize}
                    locationMetricsDefinition={locationMetricsDefinition}
                    eventMetricsDefinition={eventMetricsDefinition}
                    eventMetricsNameMapping={props.customSettings?.eventDataSettings?.metricsNameMapping}
                    eventTypeToEventStateMapping={props.customSettings?.eventDataSettings?.eventTypeToEventStateMapping}
                    eventMarkerIconsSettings={props.customSettings?.routeUISettings?.eventMarkerIconsSettings}
                    eventMarkerIconsSettingsByEventType={props.customSettings?.routeUISettings?.eventMarkerIconsSettingsByEventType}
                />
            )}

            {!tripsData && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityTripsRouteMapUIComponent', EntityTripsRouteMapUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Trips_Route_Map_Comp',
    {
        componentFunc          : EntityTripsRouteMapUIComponent,
        customSettingsValidator: isEntityTripsRouteMapUIComponentCustomSettings,
    },
);
